import i18next from 'i18next';
import { DateTime, Duration, TimeZone } from 'timezonecomplete';

import type { ITimelineHour, IWeekDay } from '@/types/calendar';
import type { IOption } from '@/types/common';

import { AM_PM, BLOCK_DURATION, BLOCKS_IN_DAY, HOUR_MINUTES, HOURS_IN_DAY, MINUTES_IN_HOUR } from '@/config/variables';
import { getTimezoneAbbreviation } from '@/helpers/timezone';

const getTimelineHours = (day: IWeekDay, timezoneName: string, step: Duration): ITimelineHour[] => {
  let dateObj = new DateTime(day.dayYear, day.monthNumber, day.dayNumber).withZone(TimeZone.zone(timezoneName));

  const arrHours: ITimelineHour[] = [];

  while (dateObj.day() === day.dayNumber) {
    const nextHourDate = dateObj.add(step);
    const comparePrevDate = dateObj.sub(Duration.hours(1));
    const compareNextDate = dateObj.add(Duration.hours(1));
    if (!day.isDlsDay) {
      arrHours.push({
        hour: dateObj.format(HOUR_MINUTES),
        note: dateObj.format(AM_PM),
      });
    } else {
      const currentTimezone = getTimezoneAbbreviation(timezoneName, dateObj.unixUtcMillis());
      const comparePrevTimezone = getTimezoneAbbreviation(timezoneName, comparePrevDate.unixUtcMillis());
      const compareNextTimezone = getTimezoneAbbreviation(timezoneName, compareNextDate.unixUtcMillis());

      const isTzChange = comparePrevTimezone !== currentTimezone || currentTimezone !== compareNextTimezone;

      arrHours.push({
        hour: dateObj.format(HOUR_MINUTES),
        note: dateObj.format(AM_PM),
        ...(isTzChange && { tz: currentTimezone }),
      });
    }

    dateObj = nextHourDate;
  }

  return arrHours;
};

const getStartTimeOptionsForDay = (day: IWeekDay, timezoneName: string, minBlock = 0): IOption[] => {
  const timelineHours = getTimelineHours(day, timezoneName, Duration.minutes(BLOCK_DURATION));
  return timelineHours.slice(minBlock).map<IOption>((item, index) => ({
    value: `${index * BLOCK_DURATION}`,
    label: `${item.hour} ${item.note.toLowerCase()} ${item.tz ? `(${item.tz})` : ''}`,
  }));
};

const getEndTimeOptionsForDay = (day: IWeekDay, timezoneName: string, minBlock = 0): IOption[] => {
  const timelineHours = getTimelineHours(day, timezoneName, Duration.minutes(BLOCK_DURATION));
  const options = timelineHours.slice(minBlock).map<IOption>((item, index) => ({
    value: `${index * BLOCK_DURATION}`,
    label: `${item.hour} ${item.note.toLowerCase()} ${item.tz ? `(${item.tz})` : ''}`,
  }));

  const len = options.length;
  let midNightValue = `${timelineHours.length * BLOCK_DURATION}`;
  if (len > 0) {
    // when it has end times, midnight value = last end time value (in minutes) + 30 minutes
    const lastHalfHour = options[len - 1];
    midNightValue = (parseInt(lastHalfHour.value) + 30).toString();
  }
  options.push({
    value: midNightValue,
    label: i18next.t`client.scheduling.sidebar.schedules.modal.midnight`,
  });

  return options;
};

const getStartTimeOptions = (minBlock = 0): { label: string; value: string }[] =>
  [...Array.from(Array(BLOCKS_IN_DAY).keys())].flatMap(item => {
    if (item < minBlock) return [];
    const valueMinutes = item * BLOCK_DURATION;
    const hh = (item - (item % 2)) / 2;
    // mm can be 30 or 0
    const mm = valueMinutes % MINUTES_IN_HOUR || '00';
    return [
      {
        label: `${hh}:${mm}`,
        value: `${valueMinutes}`,
      },
    ];
  });

const getEndTimeBaseOptions = (minBlock = 0): { label: string; value: string }[] =>
  [...Array.from(Array(BLOCKS_IN_DAY).keys())].flatMap(item => {
    if (item < minBlock) return [];
    const key = item + 1;
    const valueMinutes = key * BLOCK_DURATION;
    const hh = (key - (key % 2)) / 2;
    // mm can be 30 or 0
    const mm = valueMinutes % MINUTES_IN_HOUR || '00';
    return [
      {
        label: hh === HOURS_IN_DAY ? i18next.t`client.scheduling.sidebar.schedules.modal.midnight` : `${hh}:${mm}`,
        value: `${valueMinutes}`,
      },
    ];
  });

export {
  getStartTimeOptions,
  getStartTimeOptionsForDay,
  getEndTimeBaseOptions,
  getEndTimeOptionsForDay,
  getTimelineHours,
};
